import { useCallback } from 'react';
import type { Song, Hymn } from '../types';
import { HymnTypeahead } from './HymnTypeahead';
import { formatPhone } from '../utils/phone';

interface SongRowProps {
  song: Song;
  songIndex: number;
  hymns: Hymn[];
  getHymnDisplayText: (hymnId: string) => string;
  onUpdate: (field: keyof Song, value: string | boolean) => void;
  onRemove?: () => void;
  canRemove: boolean;
  disabled?: boolean;
}

export function SongRow({
  song,
  songIndex,
  hymns,
  getHymnDisplayText,
  onUpdate,
  onRemove,
  canRemove,
  disabled = false,
}: SongRowProps) {
  const isSacrament = songIndex === 1;

  const handleHymnChange = useCallback(
    (hymnId: string) => {
      onUpdate('hymnId', hymnId);
    },
    [onUpdate]
  );

  const handleSpecialChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdate('isSpecial', e.target.checked);
    },
    [onUpdate]
  );

  const handlePhoneInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhone(e.target.value);
      e.target.value = formatted;
      onUpdate('performerPhone', formatted);
    },
    [onUpdate]
  );

  const rowClasses = [
    'song-row',
    song.isSpecial && 'song-row--special',
    isSacrament && !song.isSpecial && 'song-row--sacrament',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div className={rowClasses}>
        <div className="song-number-label">
          <span className="song-number">#{songIndex + 1}</span>
          {isSacrament && !song.isSpecial && (
            <span className="sacrament-label">Sacrament Hymn</span>
          )}
        </div>

        <div className="song-row__input-container">
          {song.isSpecial ? (
            <input
              type="text"
              placeholder="Song Name"
              value={song.songName || ''}
              onChange={(e) => onUpdate('songName', e.target.value)}
              disabled={disabled}
            />
          ) : (
            <HymnTypeahead
              hymns={hymns}
              value={song.hymnId}
              displayValue={getHymnDisplayText(song.hymnId)}
              onChange={handleHymnChange}
              sacramentOnly={isSacrament}
              placeholder={
                isSacrament ? 'Search sacrament hymn...' : 'Search hymn...'
              }
              disabled={disabled}
            />
          )}
        </div>

        <div className="song-row__controls">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={song.isSpecial}
              onChange={handleSpecialChange}
              disabled={disabled}
            />
            <span className="special-label">Special Number</span>
          </label>
          {canRemove && onRemove && (
            <button
              className="btn-danger"
              style={{ padding: '4px 8px', fontSize: '12px' }}
              onClick={onRemove}
              disabled={disabled}
            >
              X
            </button>
          )}
        </div>
      </div>

      {song.isSpecial && (
        <div className="special-fields">
          <div>
            <label>Performer Name</label>
            <input
              type="text"
              placeholder="Performer Name"
              value={song.performerName || ''}
              onChange={(e) => onUpdate('performerName', e.target.value)}
              disabled={disabled}
            />
          </div>
          <div>
            <label>Phone Number</label>
            <input
              type="tel"
              placeholder="(123) 456-7890"
              value={song.performerPhone || ''}
              onInput={handlePhoneInput}
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </>
  );
}
